import {FC, useEffect, useState} from 'react'
import {Spinner, ProgressBar} from 'react-bootstrap'
import MatrixRain from '../../preference-loss/components/MatrixRain'

type CGESimulationLoadingProps = {
  method: 'johansen' | 'gragg'
}

// Rough expected solve durations, per the solver tooltips in CGEShockForm.
// Johansen solves in milliseconds, gragg is a non-linear multi-step solve
// (~15-60s). There's no real progress channel from the backend (single
// request/response), so this is a timer-based estimate, capped short of
// 100% until the response actually arrives.
const EXPECTED_DURATION_MS: Record<'johansen' | 'gragg', number> = {
  johansen: 1500,
  gragg: 35000,
}
const MAX_ESTIMATED_PCT = 95

const STEPS: Record<'johansen' | 'gragg', string[]> = {
  johansen: ['Loading GTAP12 bundle…', 'Applying tariff shocks…', 'Solving linear approximation…'],
  gragg: [
    'Loading GTAP12 bundle…',
    'Applying tariff shocks…',
    'Running Richardson-extrapolated Gragg solve (2/4/6 steps)…',
    'Aggregating sector and region results…',
  ],
}

const CGESimulationLoading: FC<CGESimulationLoadingProps> = ({method}) => {
  const [pct, setPct] = useState(0)
  const [step, setStep] = useState(0)

  useEffect(() => {
    setPct(0)
    const start = performance.now()
    const duration = EXPECTED_DURATION_MS[method]
    const interval = setInterval(() => {
      const elapsed = performance.now() - start
      setPct(Math.min(MAX_ESTIMATED_PCT, Math.round((elapsed / duration) * MAX_ESTIMATED_PCT)))
    }, 150)
    return () => clearInterval(interval)
  }, [method])

  useEffect(() => {
    setStep(0)
    const steps = STEPS[method]
    const t = setInterval(() => setStep((s) => (s + 1) % steps.length), 3500)
    return () => clearInterval(t)
  }, [method])

  return (
    <div className='text-white text-center rounded p-8' style={{background: '#1e1e2d'}}>
      <Spinner animation='border' variant='light' className='mb-4' />
      <h4 className='text-white mb-3'>
        Running {method} simulation… {pct}%
      </h4>
      <div className='mb-5'>{STEPS[method][step]}</div>
      <ProgressBar
        now={pct}
        style={{width: 240, margin: '0 auto'}}
        variant='primary'
        animated
        className='mb-5'
      />
      <MatrixRain />
    </div>
  )
}

export {CGESimulationLoading}
